import { createSelector } from 'reselect';

import { CheckinOrderStatus, OrderStatus } from '@websky/graphql';
import { CheckoutState } from '../../../Checkout/store/reducers';
import { CheckinState } from '../../../Modules/Checkin/store/reducers';

import { getAggregatedSummaryServices as getAggregatedSummaryServicesLib } from '../lib/getAggregatedSummaryServices';
import { checkoutSelectedServicesStateFactory } from '../../../Checkout/utils';
import { useConfig } from '../../../context';

// Checkout selectors

const getOrder = (state: CheckoutState) => state.order;

const getLoadingServicesParams = (state: CheckoutState) => state.loadingServiceParams;

/**
 * Why don't we get the current state of the selected services from state.selectedServices (see CheckoutState type)?
 * Yes, we have a state, but when we change the additional service, we dispatch the state and immediately display the current value in the UI
 * For example, when a service is removed, the number is displayed as 0, although the server response has not actually returned
 */
const getSelectedServicesState = createSelector(getOrder, checkoutSelectedServicesStateFactory);

const getIsConfirmed = createSelector(getOrder, order => order.status === OrderStatus.Confirmed);

const getAdditionalServices = createSelector(getOrder, order => order.additionalServices);

const getSegments = createSelector(getOrder, order => order.flight?.segments?.map(segment => segment.segment) ?? []);

const getSegmentsGroupCount = createSelector(getOrder, order => order.flight?.segmentGroups?.length ?? 0);

const getTravellers = createSelector(getOrder, order => order.travellers);

const getPriceToPay = createSelector(getOrder, order => order.priceToPay);

export const getAggregatedSummaryServices = createSelector(
	useConfig,
	getAdditionalServices,
	getSegments,
	getTravellers,
	getSelectedServicesState,
	getSegmentsGroupCount,
	getIsConfirmed,
	getPriceToPay,
	getLoadingServicesParams,
	getAggregatedSummaryServicesLib
);

// Checkin selectors

const getCheckinOrder = (state: CheckinState) => state.order;

const getCheckinSelectedServices = createSelector(getCheckinOrder, checkoutSelectedServicesStateFactory);

const getCheckinIsConfirmed = createSelector(getCheckinOrder, order => order.status === CheckinOrderStatus.Confirmed);

const getCheckinAdditionalServices = createSelector(getCheckinOrder, order => order.additionalServices);

const getCheckinSegments = createSelector(getCheckinOrder, order => order.segments ?? []);

const getCheckinSegmentsGroupCount = createSelector(getCheckinSegments, segments => segments.length);

const getCheckinTravellers = createSelector(getCheckinOrder, order => order.travellers);

const getCheckinPriceToPay = createSelector(getCheckinOrder, order => order.priceToPay);

export const getCheckinAggregatedSummaryServices = createSelector(
	useConfig,
	getCheckinAdditionalServices,
	getCheckinSegments,
	getCheckinTravellers,
	getCheckinSelectedServices,
	getCheckinSegmentsGroupCount,
	getCheckinIsConfirmed,
	getCheckinPriceToPay,
	getAggregatedSummaryServicesLib
);
